// Phase 3.2-C — frontend API client for stock ageing.
// Talks to /api/v1/inventory/ageing/* built in the Phase 3.2-C router.

import apiClient from '@/shared/api/client'

export type AgeingBucket = '0-30' | '31-60' | '61-90' | '91-180' | '181-365' | '365+'

export type StockStatus =
  | 'FRESH' | 'SLOW_MOVING' | 'DEAD_STOCK' | 'CRITICAL_RESERVED_STOCK'

export interface ItemAgeingRow {
  spare_id: string
  item_code: string
  item_name: string
  inventory_type: 'SPARE' | 'CONSUMABLE' | 'PACKING_MATERIAL' | 'RAW_MATERIAL'
  category: string | null
  uom: string
  current_stock: number
  average_rate: number
  stock_value: number
  oldest_receipt_date: string | null
  newest_receipt_date: string | null
  average_age_days: number
  oldest_age_days: number
  ageing_bucket: AgeingBucket
  last_issue_date: string | null
  last_receipt_date: string | null
  slow_moving_flag: boolean
  dead_stock_flag: boolean
  critical_spare_flag: boolean
  reorder_level: number
  stock_status: StockStatus
  machine_id: string | null
  department_id: string | null
}

export interface LotAgeingRow {
  stock_lot_id: string
  grn_line_id: string | null
  item_code: string
  item_name: string
  inventory_type: string
  received_qty: number
  issued_qty: number
  balance_qty: number
  landed_rate: number
  balance_value: number
  receipt_date: string | null
  age_days: number
  ageing_bucket: AgeingBucket
  warehouse: string | null
  bin_location: string | null
  spare_id: string
  machine_id: string | null
  department_id: string | null
  critical_spare_flag: boolean
}

export interface DepartmentAgeingRow {
  department_id: string | null
  department_name: string
  items: number
  total_value: number
  slow_moving_value: number
  dead_stock_value: number
  critical_reserved_value: number
  value_0_30: number
  value_31_60: number
  value_61_90: number
  value_91_180: number
  value_181_365: number
  value_365_plus: number
}

export interface MachineSpareAgeingRow {
  machine_id: string
  machine_code: string | null
  machine_name: string | null
  department_id: string | null
  spare_id: string
  spare_code: string
  spare_name: string
  current_stock: number
  balance_value: number
  oldest_age_days: number
  ageing_bucket: AgeingBucket
  last_used_date: string | null
  usage_count_90_days: number
  usage_count_180_days: number
  slow_moving_flag: boolean
  dead_stock_flag: boolean
  critical_spare_flag: boolean
  stock_status: StockStatus
}

export interface BucketValuation {
  buckets: Array<{ bucket: AgeingBucket; items: number; value: number }>
  total_value: number
}

export interface AgeingSummary {
  types: Array<{
    inventory_type: string
    total_items: number
    total_stock_value: number
    slow_moving_value: number
    dead_stock_value: number
    critical_reserved_value: number
    value_0_30: number
    value_31_60: number
    value_61_90: number
    value_91_180: number
    value_181_365: number
    value_365_plus: number
  }>
}

export const ageingApi = {
  summary: async (): Promise<AgeingSummary> => {
    const r = await apiClient.get('/inventory/ageing/summary')
    return r.data
  },
  itemWise: async (params: {
    inventory_type?: string
    department_id?: string
    machine_id?: string
    bucket?: AgeingBucket
    slow_moving_only?: boolean
    dead_stock_only?: boolean
    critical_only?: boolean
    min_value?: number
    limit?: number
  } = {}): Promise<ItemAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/item-wise', { params })
    return r.data
  },
  lotWise: async (params: {
    department_id?: string
    machine_id?: string
    bucket?: AgeingBucket
    limit?: number
  } = {}): Promise<LotAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/lot-wise', { params })
    return r.data
  },
  departmentWise: async (): Promise<DepartmentAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/department-wise')
    return r.data
  },
  machineWiseSpares: async (): Promise<MachineSpareAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/machine-wise-spares')
    return r.data
  },
  slowMoving: async (): Promise<ItemAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/slow-moving')
    return r.data
  },
  deadStock: async (): Promise<ItemAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/dead-stock')
    return r.data
  },
  criticalSpares: async (): Promise<ItemAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/critical-spares')
    return r.data
  },
  highValueOldStock: async (params: {
    min_age_days?: number
    min_value?: number
  } = {}): Promise<ItemAgeingRow[]> => {
    const r = await apiClient.get('/inventory/ageing/high-value-old-stock', { params })
    return r.data
  },
  bucketValuation: async (): Promise<BucketValuation> => {
    const r = await apiClient.get('/inventory/ageing/bucket-valuation')
    return r.data
  },
}

/**
 * Helper to fire a CSV-download for any of the above endpoints.
 * The frontend never builds the CSV — we just pull the backend-emitted
 * file as a blob and trigger a save.
 */
export async function downloadAgeingCsv(
  pathSuffix: string, params: Record<string, unknown>, filename: string,
): Promise<void> {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    search.set(k, String(v))
  }
  search.set('format', 'csv')
  const url = `/api/v1/inventory/ageing${pathSuffix}?${search.toString()}`
  const raw = localStorage.getItem('auth-storage')
  let token: string | undefined
  if (raw) {
    try { token = JSON.parse(raw)?.state?.accessToken } catch { /* ignore */ }
  }
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    throw new Error(`Export failed (${resp.status})`)
  }
  const blob = await resp.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}
